import { Injectable } from '@angular/core';
import { isNullish } from '@commonHelpers/math-utils';
import { RequestService } from '@commonServices/request.service';
import { Client as AnalyticsApi, CatalogViewAvgDto, CatalogViewDto, CatalogViewEntryPointsDto, CatalogViewEntryPointsExternalDto, CatalogViewEntryPointsInternalDto, CatalogViewPerDateDto, CatalogViewPerMonthDto, CatalogViewPerWeekDto, CatalogViewsBenchmarkDto, CatalogViewsOriginFromInternalBenchmarkDto, CatalogViewsReferrerTypeFromExternalBenchmarkDto, DatePickerRange } from '@interfaces/HttpClient/AnalyticsApiModels';
import { firstValueFrom } from 'rxjs';
import { DaterangeService } from './daterange.service';

@Injectable()
export class CatalogDashboardViewService {

	constructor(
		private analyticsApi: AnalyticsApi,
		private requestService: RequestService,
		private daterangeService: DaterangeService) { }



	async getCatalogViewsAndSessionsCountOfCatalogAsync(catalogId: number, dateRange?: DatePickerRange): Promise<CatalogViewDto> {
		try {
			if (isNullish(dateRange)) {
				return firstValueFrom(this.analyticsApi.catalogAdminView_GetCatalogViewsAndSessionsCountOfCatalog(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
			} else {
				const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
				return firstValueFrom(this.analyticsApi.catalogAdminView_GetCatalogViewsAndSessionsCountOfCatalog(catalogId, range.rangeStart, range.rangeEnd));
			}
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogViewsCountBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<CatalogViewsBenchmarkDto> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminCatalogViewsBenchmark_GetCatalogViewsCountBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogViewsCountCategoryBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<CatalogViewsBenchmarkDto> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminCatalogViewsBenchmark_GetCatalogViewsCountCategoryBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogViewsCountCategoryGroupBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<CatalogViewsBenchmarkDto> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminCatalogViewsBenchmark_GetCatalogViewsCountCategoryGroupBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogViewsCountInternalExternalPercentageAsync(catalogId: number): Promise<CatalogViewEntryPointsDto> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminView_GetCatalogViewsCountInternalExternalPercentage(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogViewsCountFromInternalAsync(catalogId: number, dateRange?: DatePickerRange): Promise<CatalogViewEntryPointsInternalDto[]> {
		try {
			if (isNullish(dateRange)) {
				return firstValueFrom(this.analyticsApi.catalogAdminView_GetCatalogViewsCountFromInternal(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
			} else {
				const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
				return firstValueFrom(this.analyticsApi.catalogAdminView_GetCatalogViewsCountFromInternal(catalogId, range.rangeStart, range.rangeEnd));
			}

		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogViewsCountOriginFromInternalBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<CatalogViewsOriginFromInternalBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminCatalogViewsBenchmark_GetCatalogViewsCountOriginFromInternalBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}
	async getCatalogViewsCountOriginFromInternalCategoryBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<CatalogViewsOriginFromInternalBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminCatalogViewsBenchmark_GetCatalogViewsCountOriginFromInternalCategoryBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}
	async getCatalogViewsCountOriginFromInternalCategoryGroupBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<CatalogViewsOriginFromInternalBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminCatalogViewsBenchmark_GetCatalogViewsCountOriginFromInternalCategoryGroupBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}


	async getCatalogViewsCountFromExternalAsync(catalogId: number, dateRange?: DatePickerRange): Promise<CatalogViewEntryPointsExternalDto[]> {
		try {
			if (isNullish(dateRange)) {
				return firstValueFrom(this.analyticsApi.catalogAdminView_GetCatalogViewsCountFromExternal(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
			} else {
				const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
				return firstValueFrom(this.analyticsApi.catalogAdminView_GetCatalogViewsCountFromExternal(catalogId, range.rangeStart, range.rangeEnd));
			}

		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogViewsCountReferrerTypeFromExternalBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<CatalogViewsReferrerTypeFromExternalBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminCatalogViewsBenchmark_GetCatalogViewsCountReferrerTypeFromExternalBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogViewsCountReferrerTypeFromExternalCategoryBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<CatalogViewsReferrerTypeFromExternalBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminCatalogViewsBenchmark_GetCatalogViewsCountReferrerTypeFromExternalCategoryBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogViewsCountReferrerTypeFromExternalCategoryGroupBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<CatalogViewsReferrerTypeFromExternalBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminCatalogViewsBenchmark_GetCatalogViewsCountReferrerTypeFromExternalCategoryGroupBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}




	async getCatalogViewsCountOfCatalogAvgAsync(catalogId: number): Promise<CatalogViewAvgDto> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminView_GetCatalogViewsCountOfCatalogAvg(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogViewsCountOfCatalogPerDateAsync(catalogId: number): Promise<CatalogViewPerDateDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminView_GetCatalogViewsCountOfCatalogPerDate(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogViewsCountOfCatalogPerWeekAsync(catalogId: number): Promise<CatalogViewPerWeekDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminView_GetCatalogViewsCountOfCatalogPerWeek(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getCatalogViewsCountOfCatalogPerMonthAsync(catalogId: number): Promise<CatalogViewPerMonthDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminView_GetCatalogViewsCountOfCatalogPerMonth(catalogId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}
}
